import { useEffect } from 'react'
import {
  useQuery,
  QueryObserverResult,
  UseQueryOptions
} from '@tanstack/react-query'
import { fetchDetails } from '@pepper/utils/src/api/projects/projects'

import { useShowMessage } from '../providers'

type ReturnType = {
  data: Project | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<QueryObserverResult<Project | undefined>>
}

export const useProjectDetails = (
  id: string,
  queryParams?: { company_id?: string; show_scrapped?: boolean },
  options?: Pick<UseQueryOptions, 'enabled'>
): ReturnType => {
  const showMessage = useShowMessage()

  const handleApiRequest = async () => {
    const {
      status,
      data: _data,
      error: _error
    } = await fetchDetails({
      id,
      queryParams
    })
    if (status === 'ok') {
      return _data
    } else {
      throw new Error(_error)
    }
  }

  const { isLoading, isFetching, isError, data, error, refetch } = useQuery<
    Project | undefined,
    Error
  >({
    queryKey: ['projects', id],
    queryFn: handleApiRequest,
    // to keep backward compatibility, we enable the query by default
    enabled: id && (options?.enabled ?? true) ? true : false
  })

  useEffect(() => {
    if (isError) {
      showMessage({ type: 'error', message: error?.message })
    }
  }, [isError, error, showMessage])

  return { data, isLoading, isFetching, error, refetch }
}
